/**
 * Frequently Bought Together Component
 * Supports both similar-products API and main recommendations API
 */

const SUPABASE_URL = 'https://bernard-recommender.bernard-legoff-29.workers.dev';

class FrequentlyBoughtTogether extends HTMLElement {
  constructor() {
    super();
    this.productGid = this.dataset.productGid;
    this.productPrice = parseFloat(this.dataset.productPrice) || 0;
    this.productTitle = this.dataset.productTitle || '';
    this.productImage = this.dataset.productImage || '';
    this.productUrl = this.dataset.productUrl || '';
    this.productVariantId = this.dataset.productVariantId;
    this.apiSource = this.dataset.apiSource || 'similar'; // 'similar' or 'recommendations'
    this.recommendedProduct = null;
    this.excludedGids = []; // Track disliked products
  }

  connectedCallback() {
    this.loadRecommendation();
  }

  async getVisitorId() {
    // Wait for theme-utils.js to initialize (up to 3 seconds)
    for (let i = 0; i < 30; i++) {
      if (window._rm && typeof window._rm.gv === 'function') {
        try {
          const vid = await window._rm.gv();
          if (vid) return vid;
        } catch (e) {
          console.warn('[FBT] Failed to get visitor ID from _rm:', e);
        }
      }
      await new Promise(r => setTimeout(r, 100));
    }
    console.warn('[FBT] Timeout waiting for visitor ID');
    return null;
  }

  async loadRecommendation() {
    if (!this.productGid) return;

    try {
      let products = [];

      if (this.apiSource === 'recommendations') {
        // Main recommendations API (visitor-based personalized recommendations)
        const visitorId = await this.getVisitorId();
        console.log('[FBT] Using recommendations API, visitorId:', visitorId);
        if (visitorId) {
          const response = await fetch(`${SUPABASE_URL}/functions/v1/recommendations`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ visitorId, limit: 10 })
          });
          if (response.ok) {
            const data = await response.json();
            const rawProducts = data.products || data || [];
            console.log('[FBT] Raw recommendations:', rawProducts.length, 'products');
            // Extract numeric ID from current product GID for comparison
            const currentProductId = this.productGid.replace(/\D/g, '');
            products = rawProducts.filter(p => {
              // Filter out the current product from recommendations
              const pId = String(p.gid || p.id || '').replace(/\D/g, '');
              const isSameProduct = pId === currentProductId;
              return !isSameProduct;
            });
            console.log('[FBT] After filtering current product:', products.length, 'products');
          } else {
            console.log('[FBT] Recommendations API failed:', response.status);
          }
        }
        // Fallback to similar-products if no visitor or no recommendations
        if (products.length === 0) {
          console.log('[FBT] Falling back to similar-products');
          const fallbackResponse = await fetch(`${SUPABASE_URL}/functions/v1/similar-products`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ productGid: this.productGid, limit: 10 })
          });
          if (fallbackResponse.ok) {
            const fallbackData = await fallbackResponse.json();
            products = fallbackData.products || fallbackData || [];
          }
        }
      } else {
        // Similar products API (product-based, no visitor ID)
        const response = await fetch(`${SUPABASE_URL}/functions/v1/similar-products`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ productGid: this.productGid, limit: 10 })
        });
        if (!response.ok) throw new Error(`API error: ${response.status}`);
        const data = await response.json();
        products = data.products || data || [];
      }

      if (!products || products.length === 0) {
        this.style.display = 'none';
        return;
      }

      // Store all products and filter out excluded ones
      this.allProducts = products;
      this.selectNextRecommendation();

    } catch (error) {
      console.error('[FBT] Error:', error);
      this.style.display = 'none';
    }
  }

  selectNextRecommendation() {
    // Find next available product not in excluded list
    const available = this.allProducts.filter(p =>
      !this.excludedGids.includes(p.gid) && !this.excludedGids.includes(p.id)
    );

    if (available.length === 0) {
      this.style.display = 'none';
      return;
    }

    this.recommendedProduct = available[0];
    this.fetchRecommendedVariant().then(() => this.render());
  }

  async switchRecommendation() {
    if (!this.recommendedProduct) return;

    // Add current to excluded list
    const gidToExclude = this.recommendedProduct.gid || this.recommendedProduct.id;
    if (gidToExclude) {
      this.excludedGids.push(gidToExclude);
    }

    this.selectNextRecommendation();
  }

  async fetchRecommendedVariant() {
    if (!this.recommendedProduct?.handle) return;

    try {
      const res = await fetch(`/products/${this.recommendedProduct.handle}.js`);
      const productData = await res.json();
      const variant = productData.variants.find(v => v.available) || productData.variants[0];
      this.recommendedProduct.variantId = variant?.id;
      this.recommendedProduct.available = variant?.available ?? false;
    } catch (e) {
      console.error('[FBT] Variant fetch error:', e);
    }
  }

  formatPrice(amount) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(amount);
  }

  getImageUrl(product, size = 200) {
    const url = product.cleanImageUrl || product.featuredImage?.url || product.images?.[0]?.url || product.images?.[0];
    if (!url) return '';
    if (url.includes('cdn.shopify.com')) {
      return url.replace(/(_\d+x\d*)|(_\d+x)/, `_${size}x`).replace(/\.(\w+)(\?|$)/, `_${size}x.$1$2`);
    }
    return url;
  }

  render() {
    const rec = this.recommendedProduct;
    const recPrice = parseFloat(rec.price?.amount || rec.price) || 0;
    const totalPrice = this.productPrice + recPrice;
    const canSwitch = this.allProducts.filter(p =>
      !this.excludedGids.includes(p.gid) && !this.excludedGids.includes(p.id)
    ).length > 1;

    this.innerHTML = `
      <div class="fbt-container">
        <div class="fbt-products">
          <div class="fbt-product">
            <a href="${this.productUrl}" class="fbt-product-image">
              <img src="${this.productImage}" alt="${this.productTitle}" loading="lazy">
            </a>
            <div class="fbt-product-info">
              <a href="${this.productUrl}" class="fbt-product-title">${this.productTitle}</a>
              <div class="fbt-product-price">${this.formatPrice(this.productPrice)}</div>
            </div>
          </div>

          <div class="fbt-plus">+</div>

          <div class="fbt-product fbt-product--recommended">
            <a href="/products/${rec.handle}" class="fbt-product-image">
              <img src="${this.getImageUrl(rec)}" alt="${rec.title}" loading="lazy">
            </a>
            <div class="fbt-product-info">
              <a href="/products/${rec.handle}" class="fbt-product-title">${rec.title}</a>
              <div class="fbt-product-price">${this.formatPrice(recPrice)}</div>
              ${!rec.available ? '<div class="fbt-out-of-stock">Épuisé</div>' : ''}
              ${canSwitch ? `
                <button type="button" class="fbt-switch-btn">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/><path d="M16 16h5v5"/></svg>
                  Changer de produit
                </button>
              ` : ''}
            </div>
          </div>
        </div>

        <div class="fbt-action">
          <div class="fbt-total">
            <span class="fbt-total-label">Prix total :</span>
            <span class="fbt-total-price">${this.formatPrice(totalPrice)}</span>
          </div>
          <button type="button" class="fbt-add-button button" ${!rec.available ? 'disabled' : ''}>
            <span class="fbt-button-text">Ajouter les 2 au panier</span>
            <span class="fbt-button-loading" style="display:none;">
              <svg class="fbt-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10" stroke-dasharray="50" stroke-dashoffset="50"><animate attributeName="stroke-dashoffset" dur="0.8s" values="50;0" repeatCount="indefinite"/></circle></svg>
              Ajout...
            </span>
          </button>
        </div>
      </div>
    `;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const addButton = this.querySelector('.fbt-add-button');
    const switchBtn = this.querySelector('.fbt-switch-btn');

    addButton?.addEventListener('click', () => this.addToCart());
    switchBtn?.addEventListener('click', () => this.switchRecommendation());
  }

  async addToCart() {
    const rec = this.recommendedProduct;
    if (!rec?.available) return;

    const items = [
      { id: parseInt(this.productVariantId, 10), quantity: 1 },
      { id: parseInt(rec.variantId, 10), quantity: 1 }
    ];

    const button = this.querySelector('.fbt-add-button');
    const buttonText = this.querySelector('.fbt-button-text');
    const buttonLoading = this.querySelector('.fbt-button-loading');

    button.disabled = true;
    buttonText.style.display = 'none';
    buttonLoading.style.display = 'inline';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!res.ok) throw new Error('Cart add failed');

      buttonText.textContent = 'Ajouté !';
      buttonText.style.display = 'inline';
      buttonLoading.style.display = 'none';

      setTimeout(() => {
        buttonText.textContent = 'Ajouter les 2 au panier';
        button.disabled = false;
      }, 2000);

      const cartDrawer = document.getElementById('Cart-Drawer');
      if (cartDrawer) {
        if (typeof cartDrawer.refresh === 'function') {
          cartDrawer.refresh();
        } else {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
        setTimeout(() => {
          if (typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }
        }, 300);
      }

    } catch (error) {
      console.error('[FBT] Add to cart error:', error);
      buttonText.textContent = 'Erreur';
      buttonText.style.display = 'inline';
      buttonLoading.style.display = 'none';
      setTimeout(() => {
        buttonText.textContent = 'Ajouter les 2 au panier';
        button.disabled = false;
      }, 2000);
    }
  }
}

if (!customElements.get('frequently-bought-together')) {
  customElements.define('frequently-bought-together', FrequentlyBoughtTogether);
}
